import { format, intervalToDuration } from "date-fns";
import { formatInTenantZone } from "@/helpers/tenantCalendar";
import { formatTenantDate } from "@/helpers/tenantDateFormat";

// 5 -> "05": a time part padded to two digits.
export const zeroPad = (value: number): string => String(value).padStart(2, "0");

export const secondsToHms = (time: number) => {
  if (!time) {
    time = 0;
  };

  time = Math.round(time);
  const hours = Math.floor(time / 3600);
  const minutes = Math.floor((time - hours * 3600) / 60);
  const seconds = Math.floor(time - hours * 3600 - minutes * 60);
  const formattedTime = new Date(0, 0, 0, hours, minutes, seconds);
  const timeFormat = hours >= 1 ? "hh:mm:ss" : "mm:ss";
  return format(formattedTime, timeFormat);
};

// 00:00:00
export const getColonFormatTime = (time: number): string => {
  time = Math.round(Number.isNaN(time) ? 0 : time);
  if (!time) return "00:00:00";

  const durationInterval = intervalToDuration({ start: 0, end: time * 1000 });
  return `${zeroPad(durationInterval.hours ?? 0)}:${zeroPad(durationInterval.minutes ?? 0)}:${zeroPad(durationInterval.seconds ?? 0)}`;
};

// 1 h 8 min 23 sec
export const getExtendedFormatTime = (time: number): string => {
  time = Math.round(Number.isNaN(time) ? 0 : time);
  if (!time) return "";

  const durationInterval = intervalToDuration({ start: 0, end: time * 1000 });
  let formatTime = "";
  if (durationInterval.hours) {
    formatTime += `${durationInterval.hours} h `;
  }
  if (durationInterval.minutes || durationInterval.hours) {
    formatTime += `${durationInterval.minutes} min `;
  }
  if (durationInterval.seconds) {
    formatTime += `${durationInterval.seconds} sec`;
  }
  return formatTime;
};

// 01h 08m 23s
export const getPaddedTimeFormat = (time: number): string => {
  time = Math.round(Number.isNaN(time) ? 0 : time);
  if (!time) return "";

  const durationInterval = intervalToDuration({ start: 0, end: time * 1000 });

  const hours = durationInterval.hours ? zeroPad(durationInterval.hours) : "";
  const minutes = durationInterval.minutes ? zeroPad(durationInterval.minutes) : "";
  const seconds = durationInterval.seconds ? zeroPad(durationInterval.seconds) : "00";

  let formattedTime = "";
  if (hours) formattedTime += `${hours}h`;
  if (minutes || hours) formattedTime += ` ${minutes}m`;
  if (seconds || minutes || hours) formattedTime += ` ${seconds}s`;

  return formattedTime.trim();
};

// Formats an API instant (UTC) on the tenant's wall clock, e.g. "Wed, 7 Oct at 02:15PM".
export const getCommonDateTimeFormat = (date: Date | string | undefined): string | undefined => {
  if (!date) return undefined;
  return formatTenantDate(date, "dayTime");
};

// File name stamped with the tenant's wall clock and that zone's UTC offset. A fixed, sortable
// stamp rather than a display format, so it is the same for every locale.
export const pdfFileName = (page: string, now: Date = new Date()) => {
  let fileName = `SpoknAI ${page} ${formatInTenantZone(now, "yyyy-MM-dd HH'h'mm'm'ss's'")} GMT`;
  const offset = formatInTenantZone(now, "xxx");
  if (offset !== "+00:00") {
    const timezone = offset.split(":");
    fileName += `${timezone[0]}h${timezone[1]}m`;
  }
  return fileName;
};

export const convertSelectedPropsToDates = <T>(
  obj: T,
  dateProps: (keyof T)[]
): T => {
  for (const key of dateProps) {
    obj[key] = new Date(obj[key] as any) as any;
  }

  return obj;
};

export const convertStringToDate = (dateString?: string, defaultValue: string = '2000-01-01T00:00:00Z'): Date => {
  return new Date(dateString || defaultValue);
};

export const formatLocalDateTime = (date: Date): string => {
  const padThreeDigits = (value: number): string => value.toString().padStart(3, "0");

  return `${date.getFullYear()}-${zeroPad(date.getMonth() + 1)}-${zeroPad(date.getDate())}T${zeroPad(date.getHours())}:${zeroPad(date.getMinutes())}:${zeroPad(date.getSeconds())}.${padThreeDigits(date.getMilliseconds())}`;
};
